import { useEffect, useRef, useState, type FocusEvent, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Sparkles, X } from 'lucide-react';

interface CustomerNameDialogProps {
  open: boolean;
  onClose: () => void;
  firstName: string;
  lastName: string;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onSubmit: () => void;
  loading?: boolean;
}

const fieldClass =
  'h-14 rounded-xl border-white/10 bg-white/[0.06] text-lg text-white placeholder:text-slate-500 ' +
  'focus-visible:ring-amber-400/60 focus-visible:ring-offset-0 disabled:opacity-50';

/**
 * Name entry as a modal pinned to the *visual* viewport.
 *
 * The kiosk page is 100dvh with overflow hidden, so when the on-screen keyboard
 * opens the browser cannot scroll a focused field into view — the inputs end up
 * behind the keyboard. `visualViewport` reports the area actually left visible,
 * and sizing the panel to it keeps the fields and the submit button reachable.
 */
export function CustomerNameDialog({
  open,
  onClose,
  firstName,
  lastName,
  onFirstNameChange,
  onLastNameChange,
  onSubmit,
  loading = false,
}: CustomerNameDialogProps) {
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const [keyboardInset, setKeyboardInset] = useState(0);

  useEffect(() => {
    if (!open) {
      setKeyboardInset(0);
      return;
    }

    const vv = window.visualViewport;

    // How much of the layout viewport the keyboard is covering.
    // Under Android's `adjustResize` the window itself shrinks, so this is 0 and
    // the scroller is already the right size. Under `adjustPan` the window keeps
    // its height and only the visual viewport shrinks — then this padding is
    // what lets the submit button be scrolled clear of the keyboard.
    const update = () => {
      const inset = vv ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop) : 0;
      setKeyboardInset(inset);
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    // Some WebViews raise the keyboard on focus without firing a resize at all.
    document.addEventListener('focusin', update);
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      document.removeEventListener('focusin', update);
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Focus after paint so the keyboard opens against the final layout.
    const id = window.setTimeout(() => firstFieldRef.current?.focus(), 120);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, loading, onClose]);

  if (!open) return null;

  const canSubmit = !loading && !!firstName.trim() && !!lastName.trim();

  /** Last resort: if the panel is still taller than the visible area, pull the
   *  focused field into view inside the scroller. */
  const keepFieldVisible = (event: FocusEvent<HTMLInputElement>) => {
    const field = event.currentTarget;
    window.setTimeout(() => {
      field.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 250);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    onSubmit();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-950/95">
      {/* Always fills the viewport and always scrolls, so the submit button stays
          reachable even if the keyboard inset can't be measured. */}
      <div className="absolute inset-0 overflow-y-auto" style={{ paddingBottom: keyboardInset }}>
        {/* Top-aligned, never centred: centring would put the fields under the keyboard. */}
        <div className="mx-auto w-full max-w-3xl px-4 pb-6 pt-4">
          <form
            onSubmit={handleSubmit}
            className="relative overflow-hidden rounded-3xl bg-slate-900 p-5 shadow-2xl shadow-slate-950/60 ring-1 ring-white/10"
          >
            <div className="pointer-events-none absolute inset-x-0 -top-24 h-40 bg-gradient-to-b from-amber-400/20 to-transparent blur-2xl" />

            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              aria-label="Kapat"
              className="absolute right-3 top-3 z-10 rounded-full p-2 text-slate-400 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-40"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="relative space-y-4">
              <div className="space-y-1 pr-10 text-center">
                <h2 className="text-2xl font-bold tracking-tight text-white">Müşteri Bilgisi</h2>
                <p className="text-sm text-slate-400">Ad ve soyad girin</p>
              </div>

              {/* One horizontal strip — both fields and the button on the same
                  row, so the whole control sits in the narrow band above the
                  on-screen keyboard instead of stacking down behind it. */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="flex-1 space-y-1.5">
                  <Label
                    htmlFor="carousel-first-name"
                    className="text-xs uppercase tracking-wider text-slate-400"
                  >
                    Ad
                  </Label>
                  <Input
                    id="carousel-first-name"
                    ref={firstFieldRef}
                    value={firstName}
                    onChange={(e) => onFirstNameChange(e.target.value)}
                    placeholder="Ad"
                    autoComplete="given-name"
                    enterKeyHint="next"
                    onFocus={keepFieldVisible}
                    disabled={loading}
                    className={fieldClass}
                    required
                  />
                </div>

                <div className="flex-1 space-y-1.5">
                  <Label
                    htmlFor="carousel-last-name"
                    className="text-xs uppercase tracking-wider text-slate-400"
                  >
                    Soyad
                  </Label>
                  <Input
                    id="carousel-last-name"
                    value={lastName}
                    onChange={(e) => onLastNameChange(e.target.value)}
                    placeholder="Soyad"
                    autoComplete="family-name"
                    enterKeyHint="go"
                    onFocus={keepFieldVisible}
                    disabled={loading}
                    className={fieldClass}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={!canSubmit}
                  className="h-14 shrink-0 rounded-2xl bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 px-6 text-lg font-bold text-slate-900 shadow-lg shadow-amber-500/25 transition-transform active:scale-[0.98] disabled:from-slate-700 disabled:via-slate-700 disabled:to-slate-700 disabled:text-slate-500 disabled:shadow-none"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Hazırlanıyor
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-5 w-5" />
                      Çevir
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
